/** SUNTRONIX ONE — Administration → Brand & Communication → Typography.
 *  Governs the GLOBAL COMMUNICATION TYPOGRAPHY STANDARD (Verdana 12pt) applied
 *  automatically across all rich-text / HTML communications. Admin-only. */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Type, ShieldCheck, RefreshCw, Wand2, FileCheck2, PenLine, FileText, Upload, Trash2, Download } from "lucide-react";

export default function Typography() {
  const [cfg, setCfg] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [sig, setSig] = useState(null);
  const [sigBusy, setSigBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/brand-typography");
      setCfg(data.config); setIsAdmin(data.is_admin);
    } catch { toast.error("Could not load typography standard"); }
  }, []);
  const loadSig = useCallback(async () => {
    try { const { data } = await api.get("/brand-typography/signature"); setSig(data.signature); }
    catch { /* ignore */ }
  }, []);
  const loadProfile = useCallback(async () => {
    try { const { data } = await api.get("/one-network/meta"); setProfile(data.company_profile || null); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); loadSig(); loadProfile(); }, [load, loadSig, loadProfile]);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const setS = (k, v) => setSig((p) => ({ ...p, [k]: v }));

  const saveSig = async () => {
    setSigBusy(true);
    try { const { data } = await api.put("/brand-typography/signature", sig); setSig(data.signature); toast.success("Signature updated — it will auto-append to every follow-up"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSigBusy(false);
  };
  const uploadProfile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Company profile must be a PDF"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/one-network/company-profile", fd);
      setProfile(data.company_profile); toast.success("Company profile uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  const removeProfile = async () => {
    if (!window.confirm("Remove the current company profile PDF?")) return;
    try { await api.delete("/one-network/company-profile"); setProfile(null); toast.success("Company profile removed"); }
    catch { toast.error("Remove failed"); }
  };
  const downloadProfile = async () => {
    try {
      const r = await api.get("/one-network/company-profile/file", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch { toast.error("Could not open the profile"); }
  };

  const save = async () => {
    setBusy(true);
    try { await api.put("/brand-typography", cfg); toast.success("Typography standard updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  const reset = async () => {
    if (!window.confirm("Reset to the SUNTRONIX default (Verdana 12pt)?")) return;
    try { const { data } = await api.post("/brand-typography/reset"); setCfg(data.config); toast.success("Reset to Verdana 12pt"); }
    catch { toast.error("Reset failed"); }
  };
  const runAudit = async () => {
    try { const { data } = await api.get("/brand-typography/audit"); setAudit(data); }
    catch { toast.error("Audit failed"); }
  };
  const migrate = async () => {
    try { const { data } = await api.post("/brand-typography/migrate"); toast.success(`Migrated ${data.migrated} · review ${data.review_required} · already clean ${data.already_clean}`); runAudit(); }
    catch { toast.error("Migration failed"); }
  };

  if (!cfg) return <div className="p-6 text-[13px] text-slate-400">Loading…</div>;
  const bodyStyle = { fontFamily: cfg.font_family, fontSize: cfg.font_size, fontWeight: cfg.body_weight };
  const grStyle = { fontFamily: cfg.font_family, fontSize: cfg.font_size, fontWeight: cfg.greeting_weight };

  return (
    <div className="max-w-4xl mx-auto px-4 py-5" data-testid="typography-page">
      <div className="flex items-center gap-2 mb-1">
        <span className="h-8 w-8 rounded-lg bg-slate-900 grid place-items-center"><Type className="h-4 w-4 text-white" /></span>
        <h1 className="text-lg font-semibold text-slate-900">Brand &amp; Communication · Typography</h1>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">One global standard applied automatically to all SUNTRONIX communications — users never pick a font manually.</p>

      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800 flex items-center gap-2 mb-4" data-testid="typography-status">
        <ShieldCheck className="h-4 w-4" /> Global standard active — <b>{cfg.font_family.split(",")[0]} {cfg.font_size}</b>, bold greeting, regular body.
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Standard</div>
          <Row label="Default font family"><Input value={cfg.font_family} onChange={(e) => set("font_family", e.target.value)} disabled={!isAdmin} data-testid="typo-font-family" /></Row>
          <Row label="Default size"><Input value={cfg.font_size} onChange={(e) => set("font_size", e.target.value)} disabled={!isAdmin} data-testid="typo-font-size" /></Row>
          <Row label="Greeting weight"><Input value={cfg.greeting_weight} onChange={(e) => set("greeting_weight", e.target.value)} disabled={!isAdmin} data-testid="typo-greeting-weight" /></Row>
          <Row label="Body weight"><Input value={cfg.body_weight} onChange={(e) => set("body_weight", e.target.value)} disabled={!isAdmin} data-testid="typo-body-weight" /></Row>
          <Row label="Fallback font"><Input value={cfg.fallback} onChange={(e) => set("fallback", e.target.value)} disabled={!isAdmin} data-testid="typo-fallback" /></Row>
          {isAdmin && (
            <div className="flex gap-2 pt-1">
              <Button onClick={save} disabled={busy} data-testid="typo-save" className="bg-slate-900 hover:bg-slate-800">Save standard</Button>
              <Button onClick={reset} variant="outline" data-testid="typo-reset"><RefreshCw className="h-4 w-4 mr-1.5" /> Reset to Verdana 12pt</Button>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Live preview</div>
          <div className="rounded-lg border border-slate-100 p-3" data-testid="typography-preview">
            <p style={grStyle}>Dear Mr. Tanaka,</p>
            <p style={bodyStyle}>It was a pleasure meeting you at <b>CES 2026</b>. I am pleased to share a brief profile of <b>SUNTRONIX</b> for your reference.</p>
            <p style={bodyStyle}>Warm regards,<br /><b>Pradeep Daga</b><br />Managing Partner<br />SUNTRONIX</p>
          </div>
          <div className="text-[11px] text-slate-400 mt-2">Greeting is bold; body is regular; selective bold for names, events &amp; references.</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 mt-4" data-testid="signature-card">
        <div className="flex items-center gap-2 mb-1">
          <PenLine className="h-4 w-4 text-slate-500" />
          <div className="text-[13px] font-semibold text-slate-900">Email signature (auto-appended to every follow-up)</div>
        </div>
        <p className="text-[12px] text-slate-500 mb-3">One admin-approved Verdana signature block. It replaces any inline sign-off so follow-ups are never double-signed. Templates set to “Outlook inserts” keep Outlook’s own signature.</p>
        {sig ? (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={!!sig.enabled} onChange={(e) => setS("enabled", e.target.checked)} disabled={!isAdmin} data-testid="sig-enabled" />
                Auto-append this signature to follow-ups
              </label>
              <Row label="Closing"><Input value={sig.closing || ""} onChange={(e) => setS("closing", e.target.value)} disabled={!isAdmin} data-testid="sig-closing" /></Row>
              <Row label="Name"><Input value={sig.name || ""} onChange={(e) => setS("name", e.target.value)} disabled={!isAdmin} data-testid="sig-name" /></Row>
              <Row label="Designation"><Input value={sig.designation || ""} onChange={(e) => setS("designation", e.target.value)} disabled={!isAdmin} data-testid="sig-designation" /></Row>
              <Row label="Company"><Input value={sig.company || ""} onChange={(e) => setS("company", e.target.value)} disabled={!isAdmin} data-testid="sig-company" /></Row>
              <Row label="Phone"><Input value={sig.phone || ""} onChange={(e) => setS("phone", e.target.value)} disabled={!isAdmin} data-testid="sig-phone" /></Row>
              <Row label="Email"><Input value={sig.email || ""} onChange={(e) => setS("email", e.target.value)} disabled={!isAdmin} data-testid="sig-email" /></Row>
              <Row label="Website"><Input value={sig.website || ""} onChange={(e) => setS("website", e.target.value)} disabled={!isAdmin} data-testid="sig-website" /></Row>
              <Row label="Tagline"><Input value={sig.tagline || ""} onChange={(e) => setS("tagline", e.target.value)} disabled={!isAdmin} data-testid="sig-tagline" /></Row>
              {isAdmin && <Button onClick={saveSig} disabled={sigBusy} data-testid="sig-save" className="bg-slate-900 hover:bg-slate-800 mt-1">Save signature</Button>}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Live preview</div>
              <div className="rounded-lg border border-slate-100 p-3" style={bodyStyle} data-testid="signature-preview">
                {sig.enabled ? (
                  <>
                    {sig.closing && <div>{sig.closing}</div>}
                    {sig.name && <div><b>{sig.name}</b></div>}
                    {[sig.designation, sig.company, sig.phone, sig.email, sig.website, sig.tagline].filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}
                  </>
                ) : <div className="text-[12px] text-slate-400">Signature auto-append is turned off. Templates keep their own sign-off.</div>}
              </div>
            </div>
          </div>
        ) : <div className="text-[12px] text-slate-400">Loading signature…</div>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 mt-4" data-testid="company-profile-card">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="h-4 w-4 text-slate-500" />
          <div className="text-[13px] font-semibold text-slate-900">Company profile PDF (ONE Network attachment)</div>
        </div>
        <p className="text-[12px] text-slate-500 mb-3">Upload the current SUNTRONIX company profile. Staff can attach it to ONE Network follow-up emails with one toggle.</p>
        {profile && profile.filename ? (
          <div className="flex flex-wrap items-center gap-2 text-[12px]">
            <Badge className="border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="profile-current">{profile.filename} · v{profile.version}</Badge>
            <span className="text-slate-400">{profile.size ? `${Math.round(profile.size / 1024)} KB` : ""}</span>
            <Button size="sm" variant="outline" onClick={downloadProfile} data-testid="profile-download"><Download className="h-3.5 w-3.5 mr-1" />Preview</Button>
            {isAdmin && <>
              <label className="inline-flex">
                <span className="inline-flex items-center h-8 px-3 rounded-md border border-slate-200 text-[12px] cursor-pointer hover:bg-slate-50" data-testid="profile-replace"><Upload className="h-3.5 w-3.5 mr-1" />{uploading ? "Uploading…" : "Replace"}</span>
                <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadProfile(e.target.files?.[0])} data-testid="profile-replace-input" />
              </label>
              <Button size="sm" variant="outline" onClick={removeProfile} data-testid="profile-remove" className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</Button>
            </>}
          </div>
        ) : (
          isAdmin ? (
            <label className="inline-flex">
              <span className="inline-flex items-center h-9 px-3 rounded-md bg-indigo-600 text-white text-[12px] cursor-pointer hover:bg-indigo-700" data-testid="profile-upload"><Upload className="h-4 w-4 mr-1.5" />{uploading ? "Uploading…" : "Upload company profile PDF"}</span>
              <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadProfile(e.target.files?.[0])} data-testid="profile-upload-input" />
            </label>
          ) : <div className="text-[12px] text-slate-400">No company profile uploaded yet.</div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 mt-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[13px] font-semibold text-slate-900">Template migration audit</div>
            <div className="text-[12px] text-slate-500">Scan email templates for foreign fonts and normalise safe ones to the standard. Wording is preserved; historical sent emails are never rewritten.</div>
          </div>
          <div className="flex gap-2">
            <Button onClick={runAudit} variant="outline" size="sm" data-testid="typo-audit"><FileCheck2 className="h-4 w-4 mr-1.5" /> Run audit</Button>
            {isAdmin && <Button onClick={migrate} size="sm" data-testid="typo-migrate" className="bg-indigo-600 hover:bg-indigo-700"><Wand2 className="h-4 w-4 mr-1.5" /> Normalise safe</Button>}
          </div>
        </div>
        {audit && (
          <div className="mt-3" data-testid="typo-audit-result">
            <div className="flex flex-wrap gap-2 mb-2 text-[12px]">
              <Badge className="border bg-slate-50 text-slate-600 border-slate-200">Total {audit.summary.total}</Badge>
              <Badge className="border bg-emerald-50 text-emerald-700 border-emerald-200">Clean {audit.summary.clean}</Badge>
              <Badge className="border bg-amber-50 text-amber-700 border-amber-200">Normalise ready {audit.summary.normalise_ready}</Badge>
              <Badge className="border bg-rose-50 text-rose-600 border-rose-200">Review required {audit.summary.review_required}</Badge>
            </div>
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {audit.templates.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-[12px] border border-slate-100 rounded px-2 py-1">
                  <span className="truncate">{t.name} <span className="text-slate-400">· {t.ownership}</span></span>
                  <span className={t.status === "CLEAN" ? "text-emerald-600" : t.status === "REVIEW_REQUIRED" ? "text-rose-600" : "text-amber-600"}>{t.status.replace(/_/g, " ")}{t.foreign_font ? ` (${t.foreign_font})` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div className="grid grid-cols-[140px_1fr] items-center gap-2">
    <div className="text-[12px] text-slate-500">{label}</div>{children}
  </div>
);
